import { ModalHeader } from '@components/tailwind-components/Modal';
import { Modal } from '@components/tailwind-components/Modal/Modal';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../../hooks/modal/useModal';
import { useModalListeners } from '../../../hooks/modal/useModalListeners';
import { modals } from './modals';

// Opinionated defaults for modals that declare modalVariant: 'v2'.
// Any field explicitly set on the modal config overrides its v2 default.
const MODAL_V2_DEFAULTS = {
  titleSize: 'text-lg',
  titleColor: 'text-gray-800',
  modalBgClass: 'bg-slate-50',
  roundedClass: 'rounded-2xl',
} as const;

export const ModalContainer: React.FC = () => {
  useModalListeners();
  const { type, hideModal, title, props } = useModal();
  const { t } = useTranslation();

  const activeModal = useMemo(() => (type ? modals[type] : null), [type]);

  const actualTitle = useMemo(
    () => title || (activeModal?.getTitle ? activeModal.getTitle(t) : ''),
    [activeModal, t, title],
  );

  const actualTitleIcon = useMemo(() => {
    if (!activeModal) {
      return undefined;
    }

    if (activeModal.getTitleIcon) {
      return activeModal.getTitleIcon;
    }

    if (
      (type === 'general-prompt-modal' || type === 'general-prompt-modal-v2') &&
      props.type
    ) {
      switch (props.type) {
        case 'delete':
          return { icon: 'delete', color: 'danger' };
        case 'confirm':
          return { icon: 'info', color: 'primary-base' };
        default:
          return { icon: 'info', color: 'primary-base' };
      }
    }

    return undefined;
  }, [activeModal, type, props]);

  // A per-call `modalSize` in props overrides the registered size, so a shared
  // modal key (e.g. general-prompt-modal-v2) can be widened for one specific use.
  const actualModalSize = useMemo(
    () => props?.modalSize ?? activeModal?.modalSize ?? 'md',
    [props, activeModal],
  );

  // Merge v2 defaults under explicit per-modal overrides
  const isV2 = activeModal?.modalVariant === 'v2';
  const resolvedBgClass =
    activeModal?.modalBgClass ??
    (isV2 ? MODAL_V2_DEFAULTS.modalBgClass : undefined);
  const resolvedTitleSize =
    activeModal?.titleSize ?? (isV2 ? MODAL_V2_DEFAULTS.titleSize : undefined);
  const resolvedTitleColor =
    activeModal?.titleColor ??
    (isV2 ? MODAL_V2_DEFAULTS.titleColor : undefined);
  const resolvedRoundedClass =
    activeModal?.roundedClass ??
    (isV2 ? MODAL_V2_DEFAULTS.roundedClass : undefined);

  return (
    <Modal
      id='conversed-modal'
      className='px-[20px] py-[20px] max-h-[calc(100dvh-100px)] flex flex-col min-h-[175px]'
      isOpen={!!activeModal}
      onClose={hideModal}
      bgClass={resolvedBgClass}
      roundedClass={resolvedRoundedClass}
      header={ModalHeader({
        content: actualTitle,
        titleIcon: actualTitleIcon,
        onClose: hideModal,
        headerMargin: activeModal?.headerMargin,
        titleSize: resolvedTitleSize,
        titleColor: resolvedTitleColor,
      })}
      headerClasses='border-b-0'
      body={activeModal?.getComponent(props)}
      bodyClasses='bg-white-50'
      size={actualModalSize}
    />
  );
};
